import type { ExperienceCategory } from "@intelligems/ig-types/dtos/experience-categories";
import {
  type PostSupportTicketRequestBodyType,
  SUPPORT_SUBJECT_MAX_LENGTH,
} from "@intelligems/ig-types/functions/support/post-support-ticket";
import { formatHumanizedDuration } from "@intelligems/ig-utils/humanizeDuration";
import { officialCategoryName } from "@intelligems/ig-utils/common";

import { supportRequesterName } from "@src/features/support/pylon-support-form";
import { EXPERIMENT_ROUTES } from "@src/routes/routes";

import { EARLY_TEST_MIN_RUNTIME_DAYS } from "./earlyTestRuntime";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export type EarlyTestAction = "end" | "pause";

export interface EarlyTestTicketExperience {
  id: string;
  name: string;
  type: string;
  category: ExperienceCategory;
}

interface EarlyTestTicketOrg {
  name?: string | null;
  domain?: string | null;
  shopId?: string | null;
}

interface EarlyTestTicketUser {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
}

interface BuildEarlyTestTicketArgs {
  experience: EarlyTestTicketExperience;
  org: EarlyTestTicketOrg;
  user: EarlyTestTicketUser;
  action: EarlyTestAction;
  /** Active runtime in days at the time of the early end/pause. */
  daysLive: number;
  /** window.location.origin, injected so the builder stays pure/testable. */
  origin: string;
  note?: string;
}

const ACTION_PAST_TENSE: Record<EarlyTestAction, string> = {
  end: "Ended",
  pause: "Paused",
};

/**
 * Builds the pre-populated support-ticket body for a test ended or paused before
 * reaching statistical significance.
 *
 * The subject leads with the store name and the body packs the test ID, deep
 * link, store identifiers, and runtime context so Support can investigate even
 * if the customer later deletes the test.
 */
export const buildEarlyTestTicketPayload = ({
  experience,
  org,
  user,
  action,
  daysLive,
  origin,
  note,
}: BuildEarlyTestTicketArgs): PostSupportTicketRequestBodyType => {
  const storeName = org.name?.trim() || "Unknown store";

  const testUrl = `${origin}${EXPERIMENT_ROUTES.Experiment}/${experience.id}`;

  const descriptionLines = [
    `${officialCategoryName(experience.category, true)} ID: ${experience.id}`,
    `${officialCategoryName(experience.category, true)} name: ${experience.name}`,
    `${officialCategoryName(experience.category, true)} link: ${testUrl}`,
    `Action taken: ${ACTION_PAST_TENSE[action]} before the recommended minimum runtime`,
    `Time live: ${formatHumanizedDuration(daysLive * MS_PER_DAY)} (recommended minimum ${EARLY_TEST_MIN_RUNTIME_DAYS} day${EARLY_TEST_MIN_RUNTIME_DAYS === 1 ? "" : "s"})`,
    `myShopify ID: ${org.shopId ?? "unknown"}`,
    `Storefront domain: ${org.domain ?? "unknown"}`,
  ];

  const trimmedNote = note?.trim();
  if (trimmedNote) {
    descriptionLines.push("", `Note from user: ${trimmedNote}`);
  }

  const subjectPrefix = `[${storeName}] Early ${action} reported: "`;
  const nameRoom = SUPPORT_SUBJECT_MAX_LENGTH - subjectPrefix.length - 1;
  const subjectName =
    experience.name.length > nameRoom
      ? `${experience.name.slice(0, nameRoom - 1)}…`
      : experience.name;

  return {
    name: supportRequesterName(user),
    subject: `${subjectPrefix}${subjectName}"`,
    description: descriptionLines.join("\n"),
    experienceIds: [experience.id],
    notAboutExperience: false,
    descriptionFormat: "plain",
    pageUrl: testUrl,
  };
};
